import * as React from 'react';
import {InventoryInfo, InventoryProperty, Unit} from './ItemMasterInterfaces';
import {getFromUnitType, getPackagingString, getUppercase} from './UomHelpers';

export interface UomTileEditProps {
  inventoryControlUser: boolean;
  isShipper: boolean;
  faiReceiveWorkflow: boolean;
  prevReviewDate: string;
  inventoryProp: InventoryProperty;
  inventoryInfo: InventoryInfo;
  onChangeProp: (next: InventoryProperty) => void;
  onChangeInfo: (patch: Partial<InventoryInfo>) => void;
}

const UomTileEdit: React.FC<UomTileEditProps> = (props: UomTileEditProps) => {
  const {onChangeProp, onChangeInfo} = props;

  const [propState, setPropState] = React.useState<InventoryProperty>(props.inventoryProp);
  const [infoState, setInfoState] = React.useState<InventoryInfo>(props.inventoryInfo);
  const [isReviewed, setIsReviewed] = React.useState<boolean>(false);

  React.useEffect(() => {
    setPropState(props.inventoryProp);
  }, [props.inventoryProp]);
  React.useEffect(() => {
    setInfoState(props.inventoryInfo);
  }, [props.inventoryInfo]);

  const updateProp = (patch: Partial<InventoryProperty>) => {
    const next = {...propState, ...patch} as InventoryProperty;
    setPropState(next);
    onChangeProp(next);
  };
  const updateInfo = (patch: Partial<InventoryInfo>) => {
    const next = {...infoState, ...patch} as InventoryInfo;
    setInfoState(next);
    onChangeInfo(patch);
  };
  const renderPalletTiHi = () => {
    return (
      <div className="item-master-tile">
        <div className="col">
          <div className="header">
            <b>{getPackagingString(propState.packaging)}</b>
          </div>
        </div>
        <div className="measurement-list">
          <MeasurementTileInput
            unit=""
            measurement="Per Layer (Ti)"
            value={infoState.cartons_per_layer}
            column_size={4}
            onChange={(v) => updateInfo({cartons_per_layer: Number(v) || 0})}
          />
          <MeasurementTileInput
            unit=""
            measurement="Layers (Hi)"
            value={infoState.layers_per_pallet}
            column_size={4}
            onChange={(v) => updateInfo({layers_per_pallet: Number(v) || 0})}
          />
          <MeasurementTileInput
            unit=""
            measurement="Net Cartons"
            value={infoState.cartons_per_pallet}
            column_size={4}
            onChange={(v) => updateInfo({cartons_per_pallet: Number(v) || 0})}
          />
        </div>
        <div className="row">
          <div className="col-sm-6 tile-line-item">
            <label style={{fontWeight: 400}}>
              <input
                type="checkbox"
                checked={!!infoState.stackable}
                onChange={(e) => updateInfo({stackable: e.target.checked})}
                style={{marginRight: 6}}
              />
            </label>
            Stackable
          </div>
        </div>
        {!props.isShipper && props.faiReceiveWorkflow && (
          <div>
            <div className="row">
              <div className="col-sm-6 tile-line-item">
                <label style={{fontWeight: 400}}>Last Reviewed:{infoState.review_date}</label>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-6 tile-line-item">
                <label style={{fontWeight: 400}}>
                  <input
                    type="checkbox"
                    checked={isReviewed}
                    onChange={(e) => {
                      setIsReviewed(e.target.checked);
                      updateInfo({
                        review_date: e.target.checked ? new Date().toISOString() : props.prevReviewDate
                      });
                    }}
                    style={{marginRight: 6}}
                  />
                </label>
                Mark As Reviewed
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  const renderBasicUomTiles = () => {
    return (
      <div>
        <div className="tile-line-item">
          <div className="row">
            <div className="col-sm-3">
              <label className="form-label">Barcode Type:</label>
              <input
                className="form-control input-sm"
                value={propState.barcode_type || ''}
                onChange={(e) => updateProp({barcode_type: e.target.value})}
                placeholder="Type"
              />
            </div>
            <div className="col-sm-9">
              <label className="form-label">Barcode:</label>
              <input
                className="form-control input-sm"
                value={propState.barcode || ''}
                onChange={(e) => updateProp({barcode: e.target.value})}
                placeholder="Barcode"
              />
            </div>
          </div>
        </div>

        <div className="measurement-list">
          <MeasurementTileInputGroup
            dimColumnSize={9}
            weightColumnSize={3}
            lengthValue={propState.length}
            widthValue={propState.width}
            heightValue={propState.height}
            dimUnit={propState.dimension_unit}
            weightValue={propState.weight}
            weightUnit={propState.weight_unit}
            onChange={(v, a, t) => {
              if (t === 'number') {
                updateProp({[a]: Number(v) || 0});
              } else {
                updateProp({[a]: v});
              }
            }}
          />
        </div>

        {(props.isShipper ||
          !props.inventoryControlUser ||
          (props.inventoryControlUser && props.faiReceiveWorkflow && propState.parent)) && (
          <div className="row">
            <div className="col-sm-6 tile-line-item">
              <label style={{fontWeight: 400}}>
                {getPackagingString(propState.packaging)} per {getPackagingString(propState.parent)}: &nbsp;
                <input
                  className="form-control input-sm"
                  type="number"
                  style={{display: 'inline-block', maxWidth: 121}}
                  value={propState.units_per_parent == null ? '' : String(propState.units_per_parent)}
                  onChange={(e) => updateProp({units_per_parent: Number(e.target.value) || 0})}
                />
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBasicPalletUom = () => {
    return (
      <div className="item-master-tile">
        <div className="col">
          <div className="header">
            <b>{getPackagingString(propState.packaging)}</b>
          </div>
          {renderBasicUomTiles()}
        </div>
      </div>
    );
  };

  const renderPalletUomTiles = () => {
    if (props.isShipper || !props.inventoryControlUser) {
      //only ti-hi
      return renderPalletTiHi();
    }
    if (props.inventoryControlUser) {
      if (props.faiReceiveWorkflow) {
        return (
          <div>
            {renderPalletTiHi()}
            {renderBasicPalletUom()}
          </div>
        );
      }
      //basic
      return renderBasicPalletUom();
    }
    return <></>;
  };

  const renderNonPalletUom = () => {
    return (
      <div className="item-master-tile">
        <div className="col">
          <div className="header">
            <b>{getPackagingString(propState.packaging)}</b>
          </div>
          <div>
            {renderBasicUomTiles()}
            {props.isShipper && (
              <div className="row">
                <div className="col-sm-6 tile-line-item">
                  <label style={{fontWeight: 400}}>
                    <input
                      type="checkbox"
                      disabled={!props.isShipper}
                      checked={!!propState.ship_alone}
                      onChange={(e) => updateProp({ship_alone: e.target.checked})}
                      style={{marginRight: 6}}
                    />
                    Shipalone
                  </label>
                </div>
              </div>
            )}
            {props.isShipper && (
              <div className="row">
                <div className="col-sm-6 tile-line-item">
                  <label style={{fontWeight: 400}}>
                    <input
                      type="checkbox"
                      disabled={!props.isShipper}
                      checked={!!propState.ship_as_is}
                      onChange={(e) => updateProp({ship_as_is: e.target.checked})}
                      style={{marginRight: 6}}
                    />
                    Ship As-Is
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return propState.packaging === 'pallet' ? renderPalletUomTiles() : renderNonPalletUom();
};

export interface MeasurementTileInputProps {
  unit: string | Unit;
  measurement: string;
  value: string | number | undefined;
  column_size: number;
  onChange: (value: string) => void;
}

export interface MeasurementTileInputGroupProps {
  dimColumnSize: number;
  weightColumnSize: number;
  lengthValue: number;
  dimUnit: string | Unit;
  weightUnit: string | Unit;
  widthValue: number;
  heightValue: number;
  weightValue: number;
  onChange: (value: string, attribute: string, type: 'number' | 'string') => void;
}

const MeasurementTileInput: React.FC<MeasurementTileInputProps> = (props: MeasurementTileInputProps) => {
  return (
    <div className={'col-sm-' + props.column_size + ' measurement-div'}>
      <div className="measurement-tile">
        <div className="measurement-edit">
          <div>{props.measurement}:</div>
          <div className="value">
            <input
              className="form-control input-sm"
              type="number"
              value={props.value === undefined || props.value === null ? '' : props.value}
              onChange={(e) => props.onChange(e.target.value)}
            />
          </div>
        </div>
        <div className="unit">{getUppercase(props.unit)}</div>
      </div>
    </div>
  );
};

const DIM_UNIT = [
  ['inches', 'in'],
  ['cm', 'cm']
];

const WEIGHT_UNIT = [
  ['lbs', 'lb'],
  ['kg', 'kg'],
  ['oz', 'oz']
];

const MeasurementTileInputGroup: React.FC<MeasurementTileInputGroupProps> = (props: MeasurementTileInputGroupProps) => {
  return (
    <div className="measurement-group">
      <div className={`measurement-tile col-sm-${props.dimColumnSize}`}>
        <div className={`col-sm-4 measurement-div`}>
          <div className="measurement-edit">
            <div>Length:</div>
            <div className="value">
              <input
                className="form-control input-sm"
                type="number"
                value={props.lengthValue === undefined || props.lengthValue === null ? '' : props.lengthValue}
                onChange={(e) => props.onChange(e.target.value, 'length', 'number')}
              />
            </div>
          </div>
        </div>
        <div className={`col-sm-4 measurement-div`}>
          <div className="measurement-edit">
            <div>Width:</div>
            <div className="value">
              <input
                className="form-control input-sm"
                type="number"
                value={props.widthValue === undefined || props.widthValue === null ? '' : props.widthValue}
                onChange={(e) => props.onChange(e.target.value, 'width', 'number')}
              />
            </div>
          </div>
        </div>
        <div className={`col-sm-4 measurement-div`}>
          <div className="measurement-edit">
            <div>Height:</div>
            <div className="value">
              <input
                className="form-control input-sm"
                type="number"
                value={props.heightValue === undefined || props.heightValue === null ? '' : props.heightValue}
                onChange={(e) => props.onChange(e.target.value, 'height', 'number')}
              />
            </div>
          </div>
        </div>
        <div className="measurement-div">
          <div className="measurement-edit">
            <div className="value">
              <select
                className="form-control"
                value={getFromUnitType(props.dimUnit)}
                onChange={(e) => props.onChange(e.target.value, 'dimension_unit', 'string')}
              >
                {getFromUnitType(props.dimUnit) === '' && <option value={''}></option>}
                {DIM_UNIT.map((unit) => (
                  <option value={unit[1]}>{unit[0]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="measurement-gap"></div>
      <div className={`measurement-tile col-sm-${props.weightColumnSize} measurement-div`}>
        <div className="measurement-edit">
          <div>Weight:</div>
          <div className="value">
            <input
              className="form-control input-sm"
              type="number"
              value={props.weightValue === undefined || props.weightValue === null ? '' : props.weightValue}
              onChange={(e) => props.onChange(e.target.value, 'weight', 'number')}
            />
          </div>
        </div>
        <div className="measurement-edit">
          <div className="value">
            <select
              className="form-control"
              value={getFromUnitType(props.weightUnit)}
              onChange={(e) => props.onChange(e.target.value, 'weight_unit', 'string')}
            >
              {WEIGHT_UNIT.map((unit) => (
                <option value={unit[1]}>{unit[0]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UomTileEdit;
